import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import type { DirectoryUser } from "../shared/types";
import {
  adminQueries,
  adminQueryKeys,
  upsertSorted,
  useAdminApi,
} from "./admin-query";
import {
  EmptyState,
  initials,
  MetricCard,
  PageHeader,
  PanelState,
  PanelToolbar,
  RefreshFailedNotice,
  type StatusFilter,
} from "./AdminUi";
import { errorMessage } from "./api";
import { formatDate } from "./admin-model";
import { Icon } from "./Icon";
import { PaginationControls, usePagination } from "./pagination";
import type { UserDraft } from "./types";
import { UserDialog } from "./UserDialog";

export function UsersPanel() {
  const queryClient = useQueryClient();
  const adminApi = useAdminApi();
  const usersQuery = useQuery(adminQueries.users());
  const users = useMemo(
    () => usersQuery.data?.users ?? [],
    [usersQuery.data?.users],
  );
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [editing, setEditing] = useState<DirectoryUser | null | undefined>(
    undefined,
  );

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return users.filter((user) => {
      const matchesStatus = status === "all" || user.status === status;
      const matchesQuery =
        !needle ||
        [user.name, user.username, user.email, ...user.groups]
          .join(" ")
          .toLowerCase()
          .includes(needle);
      return matchesStatus && matchesQuery;
    });
  }, [query, status, users]);
  const [pageSlice, setPage] = usePagination(
    filtered,
    25,
    `${query} ${status}`,
  );

  const saveMutation = useMutation({
    mutationFn: (draft: UserDraft) =>
      adminApi<{ user: DirectoryUser }>(
        editing
          ? `/api/admin/users/${encodeURIComponent(editing.id)}`
          : "/api/admin/users",
        { method: editing ? "PATCH" : "POST", body: JSON.stringify(draft) },
      ),
    onSuccess: (result) => {
      queryClient.setQueryData<{ users: DirectoryUser[] }>(
        adminQueryKeys.users,
        (current) => ({
          users: upsertSorted(
            current?.users ?? [],
            result.user,
            (user) => user.id,
            (user) => user.username,
          ),
        }),
      );
      setEditing(undefined);
    },
  });

  function edit(user: DirectoryUser | null) {
    saveMutation.reset();
    setEditing(user);
  }

  // Only a failure with nothing cached is fatal; a failed background refetch
  // keeps the panel on screen.
  if (!usersQuery.data) {
    return <PanelState error={usersQuery.isError} label="users" />;
  }

  const activeCount = users.filter((user) => user.status === "active").length;
  return (
    <>
      <PageHeader
        action={
          <button
            className="button button--primary"
            onClick={() => edit(null)}
            type="button"
          >
            <Icon name="plus" size={16} />
            Add user
          </button>
        }
        description="Manage accounts and group claims evaluated by Cloudflare Access policies."
        eyebrow="Directory"
        title="Users"
      />

      {usersQuery.isError && <RefreshFailedNotice />}

      <section className="stat-grid" aria-label="Directory summary">
        <MetricCard
          detail="Provisioned identities"
          label="Total users"
          value={users.length}
        />
        <MetricCard
          detail="Allowed to authenticate"
          label="Active"
          tone="positive"
          value={activeCount}
        />
        <MetricCard
          detail="Controlled by Access policy"
          label="Admin access"
          value="Cloudflare"
        />
      </section>

      <section className="directory-card">
        <header className="collection-header">
          <div>
            <h2>User directory</h2>
            <p>
              Credentials and group claims issued by this identity provider.
            </p>
          </div>
          <span>{users.length}</span>
        </header>
        <PanelToolbar
          filterLabel="Filter users by status"
          onQueryChange={setQuery}
          onStatusChange={setStatus}
          query={query}
          searchLabel="Search users"
          searchPlaceholder="Search name, email, group…"
          status={status}
        />
        <div className="table-wrap">
          <table className="users-table">
            <caption className="sr-only">
              Identity provider user directory
            </caption>
            <thead>
              <tr>
                <th>User</th>
                <th>Groups</th>
                <th>Status</th>
                <th>Last login</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {pageSlice.items.map((user) => (
                <tr key={user.id}>
                  <td data-label="User">
                    <div className="user-cell">
                      <span className="avatar">{initials(user)}</span>
                      <span>
                        <strong>{user.name}</strong>
                        <small>
                          {user.username} · {user.email}
                        </small>
                      </span>
                    </div>
                  </td>
                  <td data-label="Groups">
                    <div className="tag-list">
                      {user.groups.length ? (
                        user.groups.slice(0, 2).map((group) => (
                          <span className="tag" key={group}>
                            {group}
                          </span>
                        ))
                      ) : (
                        <span className="muted">—</span>
                      )}
                      {user.groups.length > 2 && (
                        <span className="tag">+{user.groups.length - 2}</span>
                      )}
                    </div>
                  </td>
                  <td data-label="Status">
                    <span className={`status status--${user.status}`}>
                      <i />
                      {user.status}
                    </span>
                  </td>
                  <td className="muted" data-label="Last login">
                    {formatDate(user.lastLoginAt)}
                  </td>
                  <td data-label="Actions">
                    <button
                      className="button button--quiet"
                      onClick={() => edit(user)}
                      type="button"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!filtered.length && (
            <EmptyState
              hint="Try a different search or status."
              onClearFilters={
                query || status !== "all"
                  ? () => {
                      setQuery("");
                      setStatus("all");
                    }
                  : undefined
              }
              title="No users found"
            />
          )}
        </div>
        <PaginationControls
          label="users"
          onPageChange={setPage}
          slice={pageSlice}
        />
      </section>

      {editing !== undefined && (
        <UserDialog
          busy={saveMutation.isPending}
          error={
            saveMutation.error
              ? errorMessage(saveMutation.error, "Unable to save user.")
              : ""
          }
          key={editing?.id ?? "new"}
          onClose={() => !saveMutation.isPending && setEditing(undefined)}
          onSave={(draft) => saveMutation.mutate(draft)}
          user={editing ?? undefined}
        />
      )}
    </>
  );
}
